/**
 * Price shown on the inquiry form. It is an estimate for the guest -- nothing
 * is charged here; the team confirms the final amount when they reply.
 */
import {
  NIGHT_HOURS,
  NIGHT_SURCHARGE_RATE,
  calculateNightSurcharge,
} from "../../../utils/meetAssist";
import { SERVICE_KEYS, TIER_VIP } from "./constants";

/**
 * Used until the Site Settings prices arrive, and if that request fails.
 * Same figures as the design.
 */
export const FALLBACK_MEET_ASSIST_PRICES = {
  meetAssistBasePrice: 21500,
  vipMeetAssistBasePrice: 86500,
  vipMeetAssistAdditionalPrice: 25300,
};

/**
 * Connecting-flight VIP has its own rate and there is no Site Settings field
 * for it yet, so it is fixed here. Move it to Site Settings if admins need to
 * change it.
 */
export const CONNECTING_VIP_PRICES = {
  firstGuestPrice: 145300,
  additionalGuestPrice: 25300,
};

/** The first-guest / additional-guest rates for one service + tier. */
export const getTierRates = (serviceKey, tier, sitePrices) => {
  const prices = { ...FALLBACK_MEET_ASSIST_PRICES, ...sitePrices };

  if (serviceKey === SERVICE_KEYS.airportConnecting) return CONNECTING_VIP_PRICES;

  if (tier === TIER_VIP) {
    return {
      firstGuestPrice: prices.vipMeetAssistBasePrice,
      additionalGuestPrice: prices.vipMeetAssistAdditionalPrice,
    };
  }

  // Standard is a flat price, however many guests there are.
  return { firstGuestPrice: prices.meetAssistBasePrice, additionalGuestPrice: 0 };
};

/**
 * True for a "HH:mm" time between 22:00 and 05:59.
 * An empty time is not night -- the guest has not picked one yet.
 */
export const isNightTime = (timeText) => {
  if (!timeText) return false;
  const hour = Number(String(timeText).split(":")[0]);
  if (!Number.isFinite(hour)) return false;
  return hour >= NIGHT_HOURS.start || hour < NIGHT_HOURS.end;
};

/**
 * Works out every number the total card shows.
 *
 * @param {Object}   args
 * @param {string}   args.serviceKey
 * @param {string}   args.tier
 * @param {Object}   args.sitePrices   - prices from Site Settings (may be empty)
 * @param {number}   args.passengerCount - adults + children
 * @param {string}   args.serviceTime  - "HH:mm", decides the night surcharge
 * @returns {{
 *   firstGuestPrice: number,
 *   additionalGuestCount: number,
 *   additionalGuestsPrice: number,
 *   isNightSurcharge: boolean,
 *   nightSurchargeRate: number,
 *   total: number,
 *   hasGuestBreakdown: boolean
 * }}
 */
export const calculateInquiryPrice = ({ serviceKey, tier, sitePrices, passengerCount, serviceTime }) => {
  const { firstGuestPrice, additionalGuestPrice } = getTierRates(serviceKey, tier, sitePrices);
  const isPerGuestPricing = additionalGuestPrice > 0;
  const isNightSurcharge = isNightTime(serviceTime);

  // Each part gets the surcharge on its own so the breakdown adds up to the total.
  const additionalGuestCount = isPerGuestPricing ? Math.max(passengerCount - 1, 0) : 0;
  const firstGuestTotal = calculateNightSurcharge(firstGuestPrice, isNightSurcharge);
  const additionalGuestsPrice =
    additionalGuestCount * calculateNightSurcharge(additionalGuestPrice, isNightSurcharge);

  return {
    firstGuestPrice: firstGuestTotal,
    additionalGuestCount,
    additionalGuestsPrice,
    isNightSurcharge,
    nightSurchargeRate: NIGHT_SURCHARGE_RATE,
    total: firstGuestTotal + additionalGuestsPrice,
    hasGuestBreakdown: isPerGuestPricing,
  };
};

/** "21,500 JPY" -- the format the whole page uses. */
export const formatYen = (amount) => `${Number(amount || 0).toLocaleString("en-US")} JPY`;
